/**
 * Per-browser session correlation tracker (issue #1447).
 *
 * `X-Request-Id` ties one HTTP request to its server log lines;
 * `X-Session-Id` ties many requests across one user's journey, including
 * across login (anon → authenticated) and across club switches.
 *
 * The id lives in `localStorage` so every tab in a browser shares it. On
 * the very first request the server mints one and echoes it back in the
 * `X-Session-Id` response header — `recordSessionIdFromResponse` captures
 * that and persists it. Subsequent requests send the persisted id back
 * via the request interceptor.
 *
 * Distinct from Silhouette's authenticator session: this is a logging /
 * analytics correlation id, not an authentication token. Losing it just
 * starts a fresh journey from the server's POV.
 */

const STORAGE_KEY = "toasthost:session-id";

let cached: string | null = null;

function readFromStorage(): string | null {
  try {
    if (typeof localStorage === "undefined") return null;
    const value = localStorage.getItem(STORAGE_KEY);
    return value && value.length > 0 ? value : null;
  } catch {
    // Safari private mode / storage disabled — fall back to in-memory.
    return null;
  }
}

function writeToStorage(value: string): void {
  try {
    if (typeof localStorage === "undefined") return;
    localStorage.setItem(STORAGE_KEY, value);
  } catch {
    /* swallow */
  }
}

// Compact base32 id: 12 chars from a 32-symbol alphabet = 60 bits. Same
// alphabet as the backend mint (SessionIdFilter.generateId — no 0/o/1/l/I,
// lowercase) so frontend- and server-minted sids look alike in logs.
// Shorter than a 36-char UUID to keep log lines compact, still well clear
// of collisions at millions of sessions. Diagnostics, not a security token.
const SID_ALPHABET = "abcdefghijkmnpqrstuvwxyz23456789";
const SID_LENGTH = 12;

function generateId(): string {
  const cryptoObj = typeof crypto !== "undefined" ? crypto : undefined;
  if (cryptoObj && typeof cryptoObj.getRandomValues === "function") {
    const bytes = new Uint8Array(SID_LENGTH);
    cryptoObj.getRandomValues(bytes);
    let out = "";
    for (let i = 0; i < SID_LENGTH; i++) {
      // Each uniformly-random byte & 0x1f is uniform over the 32 symbols.
      out += SID_ALPHABET[bytes[i] & 0x1f];
    }
    return out;
  }
  // Non-crypto fallback for environments without the Web Crypto API.
  // Sids are diagnostics, not security tokens — collisions just mix two
  // visitors' log lines, they don't grant access.
  let out = "";
  for (let i = 0; i < SID_LENGTH; i++) {
    out += SID_ALPHABET[Math.floor(Math.random() * SID_ALPHABET.length)];
  }
  return out;
}

/**
 * Get the current session id, generating + persisting one on first
 * access. The locally generated value is provisional — on the next
 * server response carrying `X-Session-Id` we replace it with whatever
 * the server settled on (the server may already hold a sid in the
 * `toasthost_sid` cookie that this browser doesn't yet know about).
 */
export function getSessionId(): string {
  if (cached) return cached;
  const fromStorage = readFromStorage();
  if (fromStorage) {
    cached = fromStorage;
    return cached;
  }
  cached = generateId();
  writeToStorage(cached);
  return cached;
}

/**
 * Persist a sid observed on a server response. Called by the request-id
 * interceptors when they see an `X-Session-Id` header. If the server
 * picked a different sid (e.g. the cookie held one from a previous tab)
 * we adopt the server's value so all tabs converge.
 */
export function recordSessionIdFromResponse(value: string | null | undefined): void {
  if (!value) return;
  if (cached === value) return;
  cached = value;
  writeToStorage(value);
}

/**
 * Forget the in-memory cache. The next `getSessionId` call rehydrates
 * from localStorage if a value is there, or mints a new one otherwise.
 *
 * Does NOT touch localStorage — call sites that want to start a brand-new
 * journey must `localStorage.removeItem(SESSION_ID_STORAGE_KEY)` themselves.
 * Splitting the two concerns keeps the rehydrate-from-storage path easy
 * to reason about in tests (set storage; call get; expect storage value)
 * without having to also reload the module.
 */
export function clearSessionId(): void {
  cached = null;
}

/** localStorage key holding the persisted sid. Exported for callers that
 * want to wipe the persisted journey alongside calling [[clearSessionId]].
 */
export const SESSION_ID_STORAGE_KEY = STORAGE_KEY;

const W = typeof window === "undefined" ? null : (window as unknown as Record<string, unknown>);
if (W) {
  // Surface the current sid to the E2E harness so the per-test failure
  // dump can record which session a flaky test ran under.
  const ns = (W.__toasthost__ as Record<string, unknown> | undefined) ?? {};
  ns.getSessionId = getSessionId;
  W.__toasthost__ = ns;
}
